/**
 * LeverHoldModal — confirm lifting (lever) an open hold (POST /qc/retenues/:id/lever, cap
 * qc.retenue.lift). Ouverte → levee; the row is retained (médico-légal — never a delete). 409 if it is
 * already levee (surfaced as a toast). Gated by useCan.
 */
import { Button, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useLabId, useLiftHold } from "../api";
import type { RetenueQc } from "../types";
import { idStr } from "../types";

export interface LeverHoldModalProps {
  open: boolean;
  retenue: RetenueQc | null;
  onClose: () => void;
  onLifted: () => void;
}

export function LeverHoldModal({ open, retenue, onClose, onLifted }: LeverHoldModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canLift = useCan(QC_CAPS.retenueLift);
  const mutation = useLiftHold(labId);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const run = () => {
    if (!canLift || !retenue) return;
    mutation.mutate(
      { retenueId: idStr(retenue.id) },
      {
        onSuccess: () => {
          toast.success(t("qc.holds.lever.success"));
          onLifted();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("qc.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("qc.holds.lever.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("qc.action.cancel")}
          </Button>
          <Button onClick={run} loading={mutation.isPending} disabled={!canLift}>
            {t("qc.holds.lever.submit")}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--nv-slate)" }}>
        {t("qc.holds.lever.confirm", { parametre: retenue?.parametre_code ?? "" })}
      </p>
    </Modal>
  );
}
